import React, { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import styled from "styled-components";
import * as uuid from "uuid";

import { AppHeader } from "../components/app_header";
import { Container, Section, Title } from "../components/styled";
import { ResourceForm } from "../components/ResourceForm";
import { ListWithAddEditRemove } from "../components/ListWithAddEditRemove";

import { League as LeagueModel } from "../model/League";
import { Coach as CoachModel } from "../model/Coach";
import { Season as SeasonModel } from "../model/Season";
import { useResource } from "../hooks/useResource";
import { useAuth } from "../hooks/useAuth";
import { createApiService } from "../services/api";

const CoachItem = styled.div`
    padding: 0.5em;
    margin: 0.25em 0;
    border: 1px solid #ccc;
    border-radius: 4px;
    background-color: #f9f9f9;
    display: flex;
    justify-content: space-between;
    align-items: center;
`;

const CoachInfo = styled.div`
    flex: 1;
`;

export const League = () => {
    const { id } = useParams();
    const { token } = useAuth();
    const [editMode, setEditMode] = useState(false);
    const [seasons, setSeasons] = useState<{ seasonId: string, seasonData: { seasonName: string } }[]>([]);
    const [seasonsLoading, setSeasonsLoading] = useState(false);
    const [seasonsError, setSeasonsError] = useState<string | null>(null);

    const {
        data,
        loading,
        error,
        update,
    } = useResource<LeagueModel>({
        resourceName: 'leagues',
        id: id!,
    });

    // Type assertion since we know it's a single resource when id is provided
    const league = data as LeagueModel | null;

    // Fetch coaches for this league using nested resource
    const {
        data: coachesData,
        loading: coachesLoading,
        error: coachesError,
        update: updateCoachInLeague,
    } = useResource<CoachModel>({
        resourceName: 'coaches',
        parentResource: 'leagues',
        parentId: id!,
        skipFetch: !id,
    });

    // Get coaches currently in the league
    const leagueCoaches = React.useMemo(() => {
        if (!coachesData) return [];
        if (Array.isArray(coachesData)) {
            return coachesData.filter(coach => coach && coach.coachId);
        }
        return [];
    }, [coachesData]);

    // Fetch season details for seasons in this league
    const fetchSeasonDetails = async (seasonIds: string[]) => {
        if (!seasonIds || seasonIds.length === 0) {
            setSeasons([]);
            return;
        }

        setSeasonsLoading(true);
        setSeasonsError(null);

        try {
            const seasonPromises = seasonIds.map(async (seasonId) => {
                try {
                    if (!seasonId) return null;
                    const apiService = createApiService<{ seasonId: string, seasonData: string }>(token!, 'seasons');
                    return await apiService.getById(seasonId);
                } catch (err) {
                    console.error(`Error fetching season ${seasonId}:`, err);
                    return null;
                }
            });

            const seasonResults = await Promise.all(seasonPromises);
            const validSeasons = seasonResults.filter((season): season is { seasonId: string, seasonData: string } => 
                season !== null && 
                season !== undefined && 
                typeof season === 'object' &&
                'seasonId' in season &&
                'seasonData' in season &&
                typeof season.seasonId === 'string' &&
                typeof season.seasonData === 'string'
            );
            
            const finalSeasons = validSeasons.map(season => {
                try {
                    if (!season.seasonData) {
                        return {
                            seasonId: season.seasonId,
                            seasonData: { seasonName: '' }
                        };
                    }
                    
                    let parsed;
                    try {
                        parsed = JSON.parse(season.seasonData);
                    } catch (parseError) {
                        console.error(`Error parsing season data for ${season.seasonId}:`, parseError);
                        return {
                            seasonId: season.seasonId,
                            seasonData: { seasonName: `Error parsing season data` }
                        };
                    }
                    
                    // Validate parsed data
                    if (!parsed || typeof parsed !== 'object') {
                        return {
                            seasonId: season.seasonId,
                            seasonData: { seasonName: `Invalid season data format` }
                        };
                    }
                    
                    return {
                        seasonId: season.seasonId,
                        seasonData: {
                            seasonName: parsed.seasonName || `Season ${season.seasonId}`,
                            divisions: Array.isArray(parsed.divisions) ? parsed.divisions : [],
                            weeks: Array.isArray(parsed.weeks) ? parsed.weeks : []
                        }
                    };
                } catch (err) {
                    console.error(`Error processing season ${season.seasonId}:`, err);
                    return {
                        seasonId: season.seasonId,
                        seasonData: { seasonName: `Error processing season` }
                    };
                }
            });
            
            setSeasons(finalSeasons);
        } catch (err) {
            const errorMessage = err instanceof Error ? err.message : 'Failed to fetch season details';
            setSeasonsError(errorMessage);
            console.error('Error fetching season details:', err);
        } finally {
            setSeasonsLoading(false);
        }
    };

    // Fetch seasons when league data changes
    useEffect(() => {
        if (league && league.seasons) {
            fetchSeasonDetails(league.seasons);
        } else {
            setSeasons([]);
        }
    }, [league]);

    if (loading && !league) {
        return (
            <Container>
                <AppHeader />
                <Section>
                    <Title>Leagues</Title>
                </Section>
                <Section>
                    Loading...
                </Section>
            </Container>
        );
    }

    if (error) {
        return (
            <Container>
                <AppHeader />
                <Section>
                    <Title data-testid="leagues-title">Leagues</Title>
                </Section>
                <Section>
                    Error: {error}
                </Section>
            </Container>
        );
    }

    if (!league) {
        return (
            <Container>
                <AppHeader />
                <Section>
                    <Title data-testid="leagues-title">Leagues</Title>
                </Section>
                <Section>
                    League not found
                </Section>
            </Container>
        );
    }

    // Add Coach Simple Form
    function AddCoachForm({ onAdd, loading }: { onAdd: (coachName: string) => void, loading?: boolean }) {
      const [inputValue, setInputValue] = useState('');
      return (
        <form onSubmit={e => { e.preventDefault(); if (inputValue) { onAdd(inputValue); setInputValue(''); } }} style={{ marginBottom: '1em' }}>
          <input
            type="text"
            value={inputValue}
            onChange={e => setInputValue(e.target.value)}
            placeholder="Enter coach name"
            style={{ width: '100%' }}
            disabled={loading}
          />
          <button type="submit" disabled={!inputValue || loading} style={{ marginTop: 8 }}>Add Coach</button>
        </form>
      );
    }

    return (
        <Container>
            <AppHeader />
            <Section>
                <Title data-testid="leagues-title">Leagues</Title>
            </Section>
            <Section>
                {!editMode ? (
                    <>
                        <h2>{league.leagueName}</h2>
                        <button onClick={() => setEditMode(true)}>Edit</button>
                    </>
                ) : (
                    <ResourceForm<LeagueModel>
                        resource={league}
                        isEditMode={true}
                        onSubmit={async (updatedLeague) => {
                            await update(league.leagueId, { ...league, ...updatedLeague });
                            setEditMode(false);
                        }}
                        onCancel={() => setEditMode(false)}
                        fields={[
                            {
                                name: 'leagueName',
                                label: 'League Name',
                                required: true,
                                placeholder: 'Enter league name',
                            },
                        ]}
                        title="Edit League"
                        submitLabel="Save"
                    />
                )}
            </Section>
            <Section>
                <Title>Coaches</Title>
                {coachesLoading && <p>Loading coaches...</p>}
                {coachesError && <p>Error loading coaches: {coachesError}</p>}
                {!coachesLoading && !coachesError && (
                  <ListWithAddEditRemove<CoachModel>
                      items={leagueCoaches}
                      getKey={c => c.coachId}
                      renderItem={c => (
                          <CoachItem>
                              <CoachInfo>
                                  <a href={`/coach/${c.coachId}`}>{c.coachName}</a>
                                  {c.record && (
                                      <div style={{ fontSize: '0.9em', color: '#666' }}>
                                          Record: {c.record.wins}W - {c.record.losses}L
                                          {c.record.ties !== undefined && ` - ${c.record.ties}T`}
                                      </div>
                                  )}
                              </CoachInfo>
                          </CoachItem>
                      )}
                      resourceFormFields={[
                          {
                              name: 'coachName',
                              label: 'Coach Name',
                              required: true,
                              placeholder: 'Enter coach name',
                          },
                      ]}
                      onAdd={async () => {}} // Add handled by custom form
                      onEdit={async (id, data) => {
                          await updateCoachInLeague(id, { ...data, coachId: id });
                      }}
                      onRemove={async (id) => {
                        // Explicit API call for nested resource
                        const apiService = createApiService<CoachModel>(token!, 'coaches', 'leagues', id!);
                        await apiService.delete(id);
                        // Refresh coaches list
                        if (typeof window !== 'undefined') window.location.reload();
                      }}
                      addTitle="Add Coach to League"
                      editTitle="Edit Coach"
                      addButtonLabel="Add +"
                      editButtonLabel="Edit"
                      submitLabel="Save"
                      renderAddForm={(_onAdd, _addMode, setAddMode) => (
                        <AddCoachForm
                          onAdd={async (coachName) => {
                            // Explicit API call for nested resource
                            const coachToAdd = {
                              coachId: coachName,
                            };
                            const apiService = createApiService<typeof coachToAdd>(token!, 'coaches', 'leagues', id!);
                            await apiService.create(coachToAdd.coachId, coachToAdd);
                            setAddMode(false);
                            // Refresh coaches list
                            if (typeof window !== 'undefined') window.location.reload();
                          }}
                          loading={coachesLoading}
                        />
                      )}
                  />
                )}
            </Section>
            <Section>
                <Title>Seasons</Title>
                {seasonsLoading && <p>Loading seasons...</p>}
                {seasonsError && <p>Error loading seasons: {seasonsError}</p>}
                {!seasonsLoading && !seasonsError && (
                    <ListWithAddEditRemove<{ seasonId: string, seasonData: { seasonName: string } }>
                        items={seasons}
                        getKey={s => s.seasonId}
                        renderItem={s => (
                            <Link to={`/seasons/${s.seasonId}`}>{s.seasonData?.seasonName || `Season ${s.seasonId}`}</Link>
                        )}
                        resourceFormFields={[
                            {
                                name: 'seasonData',
                                label: 'Season Name',
                                required: true,
                                placeholder: 'Enter season name',
                            },
                        ]}
                        onAdd={async (data) => {
                            const newSeasonId = uuid.v4();
                            const newSeason = {
                                seasonId: newSeasonId,
                                seasonData: JSON.stringify({
                                    seasonName: data.seasonData,
                                    divisions: [],
                                    weeks: []
                                }),
                                leagueId: league.leagueId,
                            };
                            const apiService = createApiService<typeof newSeason>(token!, 'seasons');
                            await apiService.create(newSeasonId, newSeason);
                            const updatedSeasons = [...(league.seasons || []), newSeasonId];
                            await update(league.leagueId, { ...league, seasons: updatedSeasons });
                        }}
                        onEdit={async (id, data) => {
                            // Only allow editing the season name
                            const apiService = createApiService<any>(token!, 'seasons');
                            const seasonToUpdate = seasons.find(s => s.seasonId === id);
                            if (seasonToUpdate) {
                                await apiService.update(id, {
                                    ...seasonToUpdate,
                                    seasonData: JSON.stringify({
                                        ...seasonToUpdate.seasonData,
                                        seasonName: data.seasonData,
                                    }),
                                    leagueId: league.leagueId,
                                });
                            }
                        }}
                        onRemove={async (id) => {
                            const apiService = createApiService<SeasonModel>(token!, 'seasons');
                            await apiService.delete(id);
                            const updatedSeasons = league.seasons?.filter(season => season !== id);
                            await update(league.leagueId, { ...league, seasons: updatedSeasons });
                        }}
                        addTitle="Add Season to League"
                        editTitle="Edit Season"
                        addButtonLabel="Add +"
                        editButtonLabel="Edit"
                        submitLabel="Save"
                    />
                )}
            </Section>
        </Container>
    );
};